import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { fetchBlogById } from "../../../api/blogsAPI";
import { FaRegCalendarAlt, FaFolder, FaUser } from "react-icons/fa";
import { placeholderImage, isValidUrl } from "../../../components/invalidImage";
import Header2 from "../../commons/Header2";
import Footer2 from "../../commons/Footer2";
import RecentBlogs from "./recentBlogs";
import RelatedBlogs from "./relatedBlogs";
import CommentSection from "./comment";
import DOMPurify from "dompurify";

// =============== Utilities (fallback transform utk post lama) ===============
const urlRegex = /(https?:\/\/[^\s<>"']+)/gim;

const isImageUrl = (u) => {
  try {
    const url = new URL(u);
    const test = (s) => /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(s);
    const path = url.pathname.toLowerCase();
    const qName =
      (url.searchParams.get("name") ||
        url.searchParams.get("filename") ||
        "").toLowerCase();
    return test(path) || test(qName);
  } catch {
    return false;
  }
};

function transformDescriptionToHtml(raw) {
  if (!raw) return raw;

  const lines = raw.split(/\r?\n/);
  const out = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(urlRegex);

    if (match && match.length === 1 && line.trim() === match[0]) {
      const url = match[0];
      if (isImageUrl(url)) {
        let caption = "";
        const next = lines[i + 1] ?? "";
        const cap = next.trim().match(/^<([^>]+)>$/);
        if (cap) {
          caption = cap[1];
          i += 1;
        }

        out.push(
          `<figure class="my-6 text-center">
             <img src="${url.replace(/"/g, "&quot;")}" alt="" loading="lazy"
                  class="blog-inline-img" />
             ${
               caption
                 ? `<figcaption class="mt-3 text-sm text-slate-500 italic">${caption
                     .replace(/</g, "&lt;")
                     .replace(/>/g, "&gt;")}</figcaption>`
                 : ""
             }
           </figure>`
        );
      } else {
        const safe = url.replace(/"/g, "&quot;");
        out.push(
          `<p><a href="${safe}" target="_blank" rel="noopener noreferrer" class="text-brand-600 underline hover:text-brand-700">${safe}</a></p>`
        );
      }
    } else {
      out.push(line);
    }
  }

  const stitched = out.join("\n").replace(urlRegex, (m) => {
    if (isImageUrl(m)) {
      return `<figure class="my-6 text-center">
                <img src="${m.replace(/"/g, "&quot;")}" alt="" loading="lazy"
                     class="blog-inline-img" />
              </figure>`;
    }
    const safe = m.replace(/"/g, "&quot;");
    return `<a href="${safe}" target="_blank" rel="noopener noreferrer" class="text-brand-600 underline hover:text-brand-700">${safe}</a>`;
  });

  return stitched;
}

// =============================== Component ===============================
const BlogDetails = () => {
  const { id: blogId } = useParams();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (blogId) {
      const getBlog = async () => {
        Swal.fire({
          title: "Loading...",
          text: "Please wait while we fetch the blog data.",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        try {
          const blogData = await fetchBlogById(blogId);
          setBlog(blogData);
          Swal.close();
        } catch (err) {
          console.error("Error fetching blog data:", err);
          setError(err.message);
          Swal.fire({ icon: "error", title: "Oops...", text: "Something went wrong!" });
        }
      };
      getBlog();
    }
  }, [blogId]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-red-500 text-lg font-overpass">{error}</p>
      </div>
    );
  if (!blog) return null;

  const htmlToRender =
    (blog.description || "").includes("<img") ||
    (blog.description || "").includes("<figure")
      ? blog.description
      : transformDescriptionToHtml(blog.description || "");

  const formattedDate = new Date(blog.created_at).toLocaleDateString("id-ID", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <style>{`
        .blog-inline-img{
          display:block;
          margin:0 auto;
          border-radius:16px;
          width:100%;
          height:auto;
          max-width:720px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
        }
        .blog-content {
          font-family: "Overpass", sans-serif;
          color: #334155;
          line-height: 1.85;
          font-size: 1.05rem;
        }
        .blog-content p,
        .blog-content li{
          text-align: justify;
          text-justify: inter-word;
          line-height: 1.85;
          margin-bottom: 1.25rem;
        }
        .blog-content h1,
        .blog-content h2,
        .blog-content h3 {
          font-family: "Oswald", sans-serif;
          color: #0f172a;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 1rem;
          line-height: 1.3;
        }
        .blog-content h1 { font-size: 2rem; }
        .blog-content h2 { font-size: 1.6rem; }
        .blog-content h3 { font-size: 1.3rem; }
        .blog-content blockquote {
          border-left: 4px solid #0284c7;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          background: linear-gradient(90deg, rgba(2,132,199,0.06) 0%, transparent 100%);
          border-radius: 0 12px 12px 0;
          font-style: italic;
          color: #475569;
        }
        .blog-content a {
          color: #0284c7;
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .blog-content a:hover { color: #0369a1; }
        .blog-content figcaption{
          text-align:center;
        }
        .blog-content ul, .blog-content ol {
          padding-left: 1.5rem;
          margin-bottom: 1.25rem;
        }
        .blog-content ul li { list-style-type: disc; }
        .blog-content ol li { list-style-type: decimal; }
      `}</style>

      <div className="bg-slate-50 min-h-screen">
        <Header2 />

        {/* Hero with featured image */}
        <section className="relative pt-28 lg:pt-32 pb-10 lg:pb-14 bg-gradient-to-br from-brand-50 via-white to-eco-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors no-underline hover:no-underline group"
              >
                <span className="inline-block transition-transform group-hover:-translate-x-1">
                  ←
                </span>
                Kembali ke Blog
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-white text-brand-700 border border-brand-100 shadow-soft mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
                Artikel
              </span>
              <h1 className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight mb-6">
                {blog.title}
              </h1>

              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-4 lg:gap-6 text-sm font-overpass text-slate-600 mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white text-sm font-bold">
                    {blog.author?.charAt(0).toUpperCase() || "D"}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Ditulis oleh</div>
                    <div className="font-semibold text-slate-800">
                      {blog.author}
                    </div>
                  </div>
                </div>
                <div className="hidden sm:block w-px h-8 bg-slate-200" />
                <div className="flex items-center gap-2 text-slate-700">
                  <FaRegCalendarAlt className="text-brand-500" />
                  <span>{formattedDate}</span>
                </div>
                <div className="hidden sm:block w-px h-8 bg-slate-200" />
                <div className="flex items-center gap-2 text-slate-700">
                  <FaFolder className="text-eco-600" />
                  <span>Blogs</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-strong">
              <img
                src={isValidUrl(blog.image) ? blog.image : placeholderImage}
                alt={blog.title}
                className="w-full h-64 sm:h-80 md:h-[420px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            </div>
          </motion.div>
        </section>

        {/* Content */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Article */}
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="lg:col-span-8"
            >
              <div className="bg-white rounded-3xl shadow-soft border border-slate-100 p-6 sm:p-10 lg:p-12">
                <div
                  className="blog-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(htmlToRender),
                  }}
                />

                {/* Share / Tag footer */}
                <div className="mt-10 pt-8 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="inline-flex items-center gap-2 text-sm text-slate-500 font-overpass">
                    <FaUser className="text-brand-500" />
                    Ditulis oleh{" "}
                    <span className="font-semibold text-slate-800">
                      {blog.author}
                    </span>
                  </div>
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold text-sm shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 transition-all hover:-translate-y-0.5 no-underline hover:no-underline"
                  >
                    Lihat Semua Artikel
                    <span>→</span>
                  </Link>
                </div>
              </div>

              {/* Comments */}
              <div className="mt-10 bg-white rounded-3xl shadow-soft border border-slate-100 p-6 sm:p-10 lg:p-12">
                <CommentSection blog={blog} setBlog={setBlog} />
              </div>
            </motion.article>

            {/* Sidebar */}
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="lg:col-span-4 space-y-6"
            >
              <div className="lg:sticky lg:top-28 space-y-6">
                <div className="bg-white rounded-3xl shadow-soft border border-slate-100 p-6">
                  <RecentBlogs />
                </div>

                {/* CTA card */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-eco-600 p-6 shadow-strong text-white">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                  <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                  <div className="relative">
                    <h3 className="font-oswald text-xl font-semibold mb-2">
                      Butuh Konsultasi?
                    </h3>
                    <p className="text-sm text-white/90 font-overpass mb-4 leading-relaxed">
                      Hubungi tim ahli kami untuk solusi pengolahan limbah cair
                      yang sesuai dengan kebutuhan Anda.
                    </p>
                    <a
                      href="https://api.whatsapp.com/send/?phone=6281211516210"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-brand-700 font-semibold text-sm hover:bg-slate-50 transition-colors no-underline hover:no-underline"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
                      </svg>
                      Chat WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </motion.aside>
          </div>
        </section>

        <RelatedBlogs />
        <Footer2 />
      </div>
    </>
  );
};

export default BlogDetails;
